// ABOUTME: The compact tracker on the list card: one segment per step on the way, coloured by the states the detail's
// ABOUTME: tracker draws (barSteps over trackerSteps), with "Step n of N" and "k done" read off those states.
import Box from '@mui/material/Box';
import { alpha, Theme } from '@mui/material/styles';
import { Text } from 'components/Text';
import { getTransWithParams } from 'modules/i18n';
import { TalentApplicationStep, TalentApplicationStepState } from '../types';
import { barSteps, doneStepCount } from '../utils';

const { DONE, CURRENT } = TalentApplicationStepState;

/** Past ten steps the segments read as texture; a tighter gap keeps them one bar and the counter carries the meaning. */
const DENSE_FROM = 11;

/** CURRENT solid brand blue, DONE the same blue faded, UPCOMING grey: the state and nothing else decides the colour. */
const segmentColor = (
  theme: Theme,
  state: TalentApplicationStepState
): string => {
  if (state === CURRENT) return theme.palette.primary.main;
  if (state === DONE) return alpha(theme.palette.primary.main, 0.4);
  return theme.palette.grey[300];
};

export const StepBar = ({ steps }: { steps: TalentApplicationStep[] }) => {
  const segments = barSteps(steps);

  if (!segments.length) {
    return null;
  }

  const dense = segments.length >= DENSE_FROM;
  const currentIndex = segments.findIndex(step => step.state === CURRENT);
  const done = doneStepCount(steps);

  return (
    <Box data-testid="step-bar" data-dense={dense ? 'true' : 'false'}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          mb: 0.75
        }}
      >
        {/* No CURRENT among the visible steps means the application sits in a step talents do not see: no counter then. */}
        {currentIndex >= 0 ? (
          <Text
            data-testid="step-bar-step-of"
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.4,
              textTransform: 'uppercase'
            }}
          >
            {getTransWithParams('applications.stepBar.stepOf', {
              current: currentIndex + 1,
              total: segments.length
            })}
          </Text>
        ) : (
          <span />
        )}
        <Text
          data-testid="step-bar-done"
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.4,
            textTransform: 'uppercase'
          }}
        >
          {getTransWithParams('applications.stepBar.done', { count: done })}
        </Text>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: dense ? '3px' : '4px',
          height: 6
        }}
      >
        {segments.map(step => {
          const isCurrent = step.state === CURRENT;
          return (
            <Box
              key={step.key}
              data-testid="step-bar-segment"
              data-step-key={step.key}
              data-state={step.state}
              aria-current={isCurrent ? 'step' : undefined}
              sx={{
                flex: 1,
                height: isCurrent ? 6 : 4,
                borderRadius: 3,
                backgroundColor: theme => segmentColor(theme, step.state)
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};
